<script context="module" lang="ts">
	import WYSIWYG from './../components/inputs/WYSIWYG.svelte';
	import type { SectionConfig } from '$lib/util/pagebuilder';
	import { gql } from 'graphql-request';
	import Media from '$lib/components/media/Media.svelte';
	import Section from '$lib/components/layout/Section.svelte';
	import DirectusLink from '$lib/components/navigation/DirectusLink.svelte';
	import Button from '$lib/components/util/Button.svelte';

	export const config: SectionConfig = {
		collection: 'section_content_hero',
		availableFor: ['pages'],
		query: gql`
        internal_name
        media {
            ...Media
        }
        translations {
            languages_code {
                code
            }
            title
            text
        }
        button_link {
            ...Link
        }
        items {
            section_content_hero_item_id {
                link {
                    ...Link
                }
            }
        }
    `
	};

	// optional: define a type for the props (note: injectTranslations is applied)
	type Props = {
		title: string;
		text: string;
		media: any;
		link: any;
		button_link: any;
		items: {
			section_content_hero_item_id: {
				link: any;
			};
		}[];
	};
</script>

<script lang="ts">
	import Wysiwyg from '$lib/components/inputs/WYSIWYG.svelte';
	import TextWithIcon from '$lib/components/util/TextWithIcon.svelte';
	import GlobalMedia from '$lib/components/media/GlobalMedia.svelte';
	import { onMount } from 'svelte';

	export let data: Props; // the components gets all data via this prop (additionally each top level prop is directly provided to the component)

	let isOverflowing = false;

	const checkOverflow = () => {
		const overviewWrapper = document.querySelector('.overview-wrapper') as HTMLElement;
		if (overviewWrapper) {
			isOverflowing = overviewWrapper.scrollWidth > overviewWrapper.clientWidth;
		}
	};

	const scrollRight = () => {
		const overviewWrapper = document.querySelector('.overview-wrapper') as HTMLElement;
		if (overviewWrapper) {
			overviewWrapper.scrollBy({ left: 200, behavior: 'smooth' });
		}
	};

	onMount(() => {
		checkOverflow();
		window.addEventListener('resize', checkOverflow);
		return () => window.removeEventListener('resize', checkOverflow);
	});
</script>

<div class="content-hero">
	<Section gapType="padding">
		<div slot="background">
			<div class="background-image-wrapper">
				<Media {...data.media} fit="cover" transform="xl" />
			</div>
		</div>
		<div class="hero-wrapper">
			<div class="title">{data.title}</div>
			<div class="divider" />
			<div class="text"><Wysiwyg text={data.text} /></div>
			{#if data.button_link}
				<DirectusLink {...data.button_link}>
					<Button {...data.button_link} style="yellow" />
				</DirectusLink>
			{/if}
		</div>
	</Section>
	<div class="overview-wrapper">
		{#each data.items as { section_content_hero_item_id: item }, idx}
			<div class="link-wrapper">
				<DirectusLink {...item.link}>
					<span class="number">0{idx + 1}</span><TextWithIcon {...item.link} />
				</DirectusLink>
			</div>
		{/each}
		{#if isOverflowing}
			<div class="scroll-arrow" on:click={scrollRight}><GlobalMedia key="icon_caret_grey" /></div>
		{/if}
	</div>
</div>

<style lang="scss">
	.content-hero {
		padding: v(content-padding-xl);
		@include media(tablet) {
			padding: v(content-padding-sm);
		}
	}
	.content-hero :global(section) {
		min-height: 60vh;
		display: flex;
		flex-direction: column;
		justify-content: center;
	}
	.hero-wrapper {
		height: 100%;
		display: flex;
		flex-direction: column;
		justify-content: center;
		gap: v(content-padding);

		@include media(tablet) {
			padding-inline-start: v(content-padding-sm);
		}
	}
	.title {
		@include font(h1);
		color: color(white);
		margin-bottom: v(content-gap);
		max-width: 12ch;
	}
	.text {
		@include font(text-lg);
		color: color(white);
		max-width: 30ch;
		text-wrap: balance;
	}
	.divider {
		width: 2.8rem;
		height: 3px;
		background: color(white);
		margin-top: -0.1rem;
	}

	.background-image-wrapper {
		position: absolute;
		width: 100%;
		height: 100%;
		z-index: -1;
		filter: brightness(0.7);
	}
	.overview-wrapper {
		display: flex;
		justify-content: space-around;
		padding-top: v(content-padding-xl);
		white-space: nowrap;
		gap: v(content-gap);
		overflow-x: auto;
		scroll-behavior: smooth;
		::-webkit-scrollbar {
			display: none;
		}
		-ms-overflow-style: none; /* IE and Edge */
		scrollbar-width: none; /* Firefox */
		@include media(tablet) {
			padding-top: v(content-gap);
			padding-bottom: v(content-gap-xs);
			justify-content: space-between;
		}
		.scroll-arrow {
			display: flex;
			background: transparent;
			position: absolute;
			inset-inline-end: 0;
			z-index: 1;
			width: 14px;
			height: 14px;
		}

		.link-wrapper {
			display: flex;
			align-items: center;
			&:hover {
				color: color(grey_06) !important;
				:global(.text-with-icon) {
					border-bottom: 2px solid transparent !important;
					color: color(grey_06);
				}
				.number {
					color: color(grey_06);
				}
			}
		}
		.number {
			@include font(h6);
			color: color(black);
			padding-inline-end: v(content-gap-xs);
			display: flex;
			align-items: flex-start;
			padding-bottom: 2px;
			height: 100%;
			transition: all 0.3s ease;
		}
	}
</style>
